import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, TextField, InputAdornment, Stack } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useDraggable } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuIcon from '@mui/icons-material/Menu';

import { neutral } from '_lex/_foundations/phxColors';
import { useFields } from 'views/App/Portal/PerformanceReporting/FieldBank/_data_sources/useFields';
import { Field, FieldTypeEnum, FieldStatus } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import FieldBankFilters, { FieldBankFiltersParams } from 'views/App/Portal/PerformanceReporting/FieldBank/List/FieldBankFilters';
import FiltersGroup from 'views/App/Portal/PerformanceReporting/FieldBank/List/FiltersGroup';
import { DragData } from '../../_types';
import { useTemplateBuilderContext } from '../context';
import { getCategoryColor } from 'views/App/Portal/PerformanceReporting/_utils';
// Field type icons mapping - using actual MUI icons
const getFieldTypeIcon = (fieldType: FieldTypeEnum) => {
  switch (fieldType) {
    case 'Text':
      return <TextFieldsIcon fontSize='small' />;
    case 'Number':
      return <NumbersIcon fontSize='small' />;
    case 'Boolean':
      return <CheckBoxIcon fontSize='small' />;
    case 'Date':
      return <CalendarTodayIcon fontSize='small' />;
    case 'Time':
      return <AccessTimeIcon fontSize='small' />;
    case 'SingleSelect':
      return <MenuIcon fontSize='small' />;
    case 'Lookup':
      return <SearchIcon fontSize='small' />;
    default:
      return <TextFieldsIcon fontSize='small' />;
  }
};

interface DraggableFieldItemProps {
  field: Field;
  isOverDropZone: boolean;
}

const DraggableFieldItem: React.FC<DraggableFieldItemProps> = ({ field, isOverDropZone }) => {
  const dragData: DragData = {
    type: 'field-from-bank',
    field,
  };

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `field-bank-${field.id}`,
    data: dragData,
  });

  // Get category color for this field
  const categoryColor = getCategoryColor(field.fieldCategory);

  const style = {
    opacity: isDragging ? 0.5 : 1,
    pointerEvents: isOverDropZone ? ('none' as const) : ('auto' as const),
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: '1px solid',
        borderColor: neutral[200],
        borderLeft: '4px solid',
        borderLeftColor: categoryColor,
        borderRadius: 1,
        backgroundColor: 'background.paper',
        cursor: 'default',
        width: '100%', // Force consistent width
        maxWidth: '100%', // Prevent expansion
        minWidth: 0, // Allow shrinking if needed
        boxSizing: 'border-box', // Include padding and border in width calculation
        '&:hover': {
          backgroundColor: neutral[25],
          borderColor: neutral[300],
          borderLeftColor: categoryColor, // Keep category color on hover
        },
        transition: 'all 0.2s ease',
      }}
      // Removed {...attributes} and {...listeners} from here
    >
      {/* Field type icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
          color: categoryColor,
        }}
      >
        {getFieldTypeIcon(field.fieldType)}
      </Box>

      {/* Field info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant='body2'
          fontWeight='600'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontSize: '0.875rem',
          }}
        >
          {field.name}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            fontSize: '0.75rem',
            mt: 0.25,
          }}
        >
          {field.fieldCategory?.name || 'Demographics'}
        </Typography>
      </Box>

      {/* Drag handle - ONLY this area is draggable */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: neutral[400],
          flexShrink: 0,
          cursor: isDragging ? 'grabbing' : 'grab',
          p: 0.5,
          borderRadius: 0.5,
          '&:hover': {
            backgroundColor: neutral[100],
            color: neutral[600],
          },
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize='small' />
      </Box>
    </Box>
  );
};

// Define a compatible type that matches FieldBankFiltersParams
type FieldBankPanelParams = FieldBankFiltersParams;

const FieldBankPanel: React.FC = () => {
  const { isOverDropZone } = useTemplateBuilderContext();
  const [searchTerm, setSearchTerm] = useState('');

  // Add filtering state using compatible type - status is always 'Active' for template builder
  const [filterParams, setFilterParams] = useState<FieldBankPanelParams>({
    search: null,
    status: 'Active', // Always filter for active fields in template builder
    fieldTypes: null,
    fieldCategoryIds: null,
  });

  // Update params callback for filters - always preserve Active status for template builder
  const updateFilterParams = useCallback((updatedParams: FieldBankFiltersParams) => {
    setFilterParams((prev) => ({
      ...prev,
      ...updatedParams,
      status: 'Active', // Always keep status as Active in template builder
    }));
  }, []);

  // Clear params callback for filters - always preserve Active status for template builder
  const clearFilterParams = useCallback(() => {
    setFilterParams({
      search: null,
      status: 'Active', // Always keep status as Active in template builder
      fieldTypes: null,
      fieldCategoryIds: null,
    });
  }, []);

  // Combine all params for FiltersGroup component (includes status for internal use)
  const allParams = useMemo(
    () => ({
      search: searchTerm || null,
      ...filterParams,
    }),
    [searchTerm, filterParams]
  );

  // Template builder specific params (includes status but will be hidden in UI)
  const templateBuilderParams = useMemo(
    () => ({
      search: searchTerm || null,
      status: filterParams.status,
      fieldTypes: filterParams.fieldTypes,
      fieldCategoryIds: filterParams.fieldCategoryIds,
    }),
    [searchTerm, filterParams.status, filterParams.fieldTypes, filterParams.fieldCategoryIds]
  );

  // Params for FiltersGroup (excludes status and search to avoid showing unnecessary chips)
  const filtersGroupParams = useMemo(
    () => ({
      fieldTypes: filterParams.fieldTypes,
      fieldCategoryIds: filterParams.fieldCategoryIds,
    }),
    [filterParams.fieldTypes, filterParams.fieldCategoryIds]
  );

  // Combine search and filter parameters for API query
  const queryParams = useMemo(() => {
    const params: any = {
      search: searchTerm || undefined,
      limit: 25, // Limit to 25 items per page as specified in requirements
    };

    // Add filter parameters if they exist
    if (filterParams.status) {
      params.status = filterParams.status;
    }
    if (filterParams.fieldTypes && filterParams.fieldTypes.length > 0) {
      params.fieldTypes = filterParams.fieldTypes.join(',');
    }
    if (filterParams.fieldCategoryIds && filterParams.fieldCategoryIds.length > 0) {
      params.fieldCategoryIds = filterParams.fieldCategoryIds.join(',');
    }

    return params;
  }, [searchTerm, filterParams]);

  const fieldsQuery = useFields(queryParams);
  const allFields = fieldsQuery.data?.pages.flatMap((page) => page.content) || [];
  const { isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = fieldsQuery;

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle scroll to load more
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
        height: '100%',
        gap: 2,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden', // Prevent content from expanding beyond container
      }}
    >
      {/* Search field with filter button */}
      <Box sx={{ p: 3, backgroundColor: neutral[50], borderRadius: 3 }}>
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
        >
          <TextField
            fullWidth
            size='small'
            placeholder={t('performanceReporting.templates.builder.fieldBank.search.placeholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              paddingTop: 0,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />
          <FieldBankFilters
            params={templateBuilderParams}
            updateParams={updateFilterParams}
            hideStatusFilter={true}
            showClearAll={true}
            onClearAll={() => {
              setSearchTerm('');
              clearFilterParams();
            }}
          />
        </Stack>
      </Box>

      {/* Active filters display */}
      <Box sx={{ px: 3 }}>
        <FiltersGroup
          params={filtersGroupParams}
          updateParams={(updatedParams) => {
            // Convert to FieldBankFiltersParams format, preserving Active status
            const filterParams: FieldBankFiltersParams = {
              status: 'Active', // Always keep Active status for template builder
              fieldTypes: updatedParams.fieldTypes,
              fieldCategoryIds: updatedParams.fieldCategoryIds,
            };
            updateFilterParams(filterParams);
          }}
          clearParams={() => {
            clearFilterParams();
          }}
          consolidatedView={true}
        />
      </Box>

      {/* Fields list */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 3,
          pb: 3,
          minWidth: 0, // Prevent width expansion
          maxWidth: '100%', // Constrain width
        }}
        onScroll={handleScroll}
      >
        {isLoading && allFields.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
            <Typography variant='body2'>Error loading fields</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              minWidth: 0, // Prevent content from expanding container
              width: '100%', // Ensure consistent width
            }}
          >
            {allFields.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isOverDropZone={isOverDropZone}
              />
            ))}

            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {!hasNextPage && allFields.length > 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                >
                  {t('performanceReporting.templates.builder.fieldBank.filters.noMoreFields')}
                </Typography>
              </Box>
            )}

            {allFields.length === 0 && !isLoading && (
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  minHeight: '300px',
                }}
              >
                <Box
                  component='img'
                  src='/admin/img/Illus.png'
                  alt='No results found'
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 3,
                    opacity: 0.7,
                  }}
                />
                <Typography
                  color='text.secondary'
                  variant='body2'
                  sx={{
                    fontSize: '1.0rem',
                    fontWeight: 400,
                    lineHeight: 1.3,
                    maxWidth: '200px',
                    wordWrap: 'break-word',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                  }}
                >
                  {t('performanceReporting.templates.builder.fieldBank.filters.noResults')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldBankPanel;
